import {
  HStack,
  Textarea,
  IconButton,
  Icon,
  Box,
  VStack,
  Text,
  Flex,
  FormLabel,
  Badge,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { RiAddLine } from "react-icons/ri";

interface PlanQNAProps {
  questions: string[];
  setQuestions: (questions: string[]) => void;
  onSave?: () => void;
  showSaveButton?: boolean;
}

const PlanQNA: React.FC<PlanQNAProps> = ({ 
  questions, 
  setQuestions, 
  onSave,
  showSaveButton = true 
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<string>("");

  const handleAddQuestion = () => {
    if (currentQuestion.trim()) {
      setQuestions([...questions, currentQuestion.trim()]);
      setCurrentQuestion("");
    }
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...questions];
    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);
  };

  return (
    <Box width="100%" maxH="100%" border="1px" borderColor="gray.200" borderRadius="md" p={2}>
      {/* Header */}
      <Box mb={4} flexShrink={0} p={2}>
        <HStack spacing={2} mb={2}>
          <FormLabel fontSize="md" mb={0} fontWeight="bold" color="gray.700">
            Plan Questions
          </FormLabel>
          {questions.length > 0 && (
            <Badge colorScheme="purple" bg="purple.700" color="white"  textTransform="none">
              {questions.length} {questions.length > 1 ? "Questions" : "Question"}
            </Badge>
          )}
        </HStack>
      </Box>
      {/* Questions Display Area */}
      <Box
        height="lg"
        borderRadius="md"
        overflowY="auto"
        sx={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "2px",
          },
        }}
      >
        {questions.length > 0 ? (
          <VStack spacing={2} align="stretch">
            {questions.map((question, index) => (
              <Flex
                key={index}
                bg="white"
                px={2}
                py={1}
                borderRadius="md"
                justify="space-between"
                align="flex-start"
                boxShadow="md"
                _hover={{ boxShadow: "md" }}
                transition="box-shadow 0.2s"
              >
                <Box>
                  <Text fontSize="xs" color="purple.500" align="left" fontWeight="bold">Q{index + 1}</Text>
                  <Text align="left" fontSize="sm" flex="1" lineHeight="1.5" pr={0} fontWeight="600">
                    {question}
                  </Text>
                </Box>
                <IconButton
                  icon={<Icon as={FaTimes} boxSize={3} _hover={{boxSize: 4}} />}
                  aria-label="Remove Question"
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemoveQuestion(index)}
                  flexShrink={0}
                />
              </Flex>
            ))}
          </VStack>
        ) : (
          <Flex h="150px" align="center" justify="center">
            <Text color="gray.500" fontSize="sm" textAlign="center">
              No questions added yet.<br />
              Add your first question below.
            </Text>
          </Flex>
        )}
      </Box>

      {/* Input Area */}
      <VStack spacing={3} align="stretch">
        <HStack spacing={3} m={1}>
          <Textarea
            placeholder="Type your question..."
            resize="none"
            flex={1}
            borderColor="gray.400"
            focusBorderColor="purple.400"
            fontSize="sm"
            value={currentQuestion}
            onChange={(e) => setCurrentQuestion(e.target.value)}
            bg="white"
          />
          <IconButton
            icon={<Icon as={RiAddLine} boxSize={5} />}
            aria-label="Add Question"
            borderRadius="full"
            size="md"
            colorScheme="purple"
            variant="solid"
            onClick={handleAddQuestion}
            isDisabled={!currentQuestion.trim()}
            flexShrink={0}
          />
        </HStack>
      </VStack>
    </Box>
  );
};

export default PlanQNA;
